import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getPermissions, deletePermission } from '../../../redux/Slices/PermissionsSlice';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../shared/hooks/TitleContext';
import i18n from '../../../i18n/i18n';

const Permissions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  const [permissions, setPermissions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    setTitle('sidenav.permissions');
  }, [setTitle]);

  const fetchPermissions = async (page = currentPage, search = searchTerm) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: 10,
      };
      if (search) params.search = search;
      const result = await dispatch(getPermissions(params));

      if (getPermissions.fulfilled.match(result)) {
        const response = result.payload;

        // Show offline toast if data came from cache
        if (response?.isOfflineCache) {
          toast.info(isRTL ? 'يتم عرض البيانات من الذاكرة المؤقتة (أوفلاين)' : 'Showing data from offline cache', {
            toastId: 'permissions-offline',
            autoClose: 2000
          });
        }

        // Robust data extraction for both Online and Offline structures
        let permissionsList = [];
        let paginationData = null;

        if (response && Array.isArray(response.data)) {
          // Standard structure: { data: [], meta: {} }
          permissionsList = response.data;
          paginationData = response.meta || response.pagination || null;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          // Nested structure: { data: { data: [], current_page: ... } }
          permissionsList = response.data.data;
          paginationData = response.data.meta || response.data || null;
        } else if (Array.isArray(response)) {
          // Array only structure
          permissionsList = response;
        }

        setPermissions(permissionsList);
        setPagination(paginationData);
      } else {
        setError(result.payload || 'Failed to fetch permissions');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch permissions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchPermissions(1, searchTerm);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleAdd = () => {
    navigate('/permissions/add');
  };

  const handleEdit = (id) => {
    navigate(`/permissions/edit/${id}`);
  };

  const handleDelete = (permission) => {
    setPermissionToDelete(permission);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (permissionToDelete) {
      try {
        const result = await dispatch(deletePermission(permissionToDelete.id));
        if (deletePermission.fulfilled.match(result)) {
          toast.success(t('permissions.delete.success', { defaultValue: 'تم حذف الصلاحية بنجاح' }), { rtl: isRTL });
          setShowDeleteModal(false);
          setPermissionToDelete(null);
          fetchPermissions(currentPage, searchTerm);
        } else {
          toast.error(result.payload || t('permissions.delete.error', { defaultValue: 'فشل حذف الصلاحية' }), { rtl: isRTL });
        }
      } catch (err) {
        toast.error(err.message || t('permissions.delete.error', { defaultValue: 'فشل حذف الصلاحية' }), { rtl: isRTL });
      }
    }
  };

  const totalPages = pagination?.last_page || 1;
  const currentPageSafe = pagination?.current_page || currentPage || 1;

  if (isLoading && permissions.length === 0) {
    return (
      <div style={{
        padding: '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {t('common.loading', { defaultValue: 'جاري التحميل...' })}
      </div>
    );
  }

  return (
    <div style={{
      padding: '30px',
      backgroundColor: '#14263b',
      minHeight: 'calc(100vh - 80px)',
      color: 'white',
      direction: dir,
      textAlign
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
          {t('sidenav.permissions', { defaultValue: 'الصلاحيات' })}
        </h1>
        <button
          onClick={handleAdd}
          style={{
            backgroundColor: '#AC2000',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaPlus /> {t('permissions.add', { defaultValue: 'إضافة صلاحية' })}
        </button>
      </div>

      {/* Search */}
      <div style={{
        marginBottom: '20px'
      }}>
        <input
          type="text"
          placeholder={t('permissions.searchPlaceholder', { defaultValue: 'بحث بالصلاحيات...' })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 15px',
            backgroundColor: '#1b3350',
            border: '1px solid #333',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            direction: dir,
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #333'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '600px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  {t('permissions.table.number', { defaultValue: '#' })}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  {t('permissions.table.name', { defaultValue: 'اسم الصلاحية' })}
                </th>
                <th style={{
                  padding: '15px',
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  {t('permissions.table.actions', { defaultValue: 'الإجراءات' })}
                </th>
              </tr>
            </thead>
            <tbody>
              {permissions && permissions.length > 0 ? (
                permissions.map((permission, index) => (
                  <tr key={permission.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      {(currentPageSafe - 1) * 10 + index + 1}
                    </td>
                    <td style={{
                      padding: '15px',
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      {permission.translation || permission.name}
                    </td>
                    <td style={{
                      padding: '15px',
                      textAlign: 'center'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                        <button
                          onClick={() => handleEdit(permission.id)}
                          style={{
                            backgroundColor: '#B3B3B3',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('btns.edit', { defaultValue: 'تعديل' })}
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(permission)}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '8px 10px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0
                          }}
                          title={t('btns.delete', { defaultValue: 'حذف' })}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', color: 'white', padding: '20px' }}>
                    {t('permissions.noData', { defaultValue: 'لا توجد صلاحيات' })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', paddingBottom: '20px' }}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
              disabled={currentPageSafe === 1}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPageSafe === 1 ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              {t('common.previous', { defaultValue: 'السابق' })}
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page =>
                page === 1 ||
                page === totalPages ||
                (page >= currentPageSafe - 2 && page <= currentPageSafe + 2)
              )
              .map((page, idx, arr) => {
                if (idx > 0 && page - arr[idx - 1] > 1) {
                  return (
                    <React.Fragment key={`ellipsis-${page}`}>
                      <span style={{ padding: '8px', color: 'white' }}>...</span>
                      <button
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: page === currentPageSafe ? '#AC2000' : '#1a1f2e',
                          color: 'white',
                          border: '1px solid #333',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: page === currentPageSafe ? '#AC2000' : '#1a1f2e',
                      color: 'white',
                      border: '1px solid #333',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {page}
                  </button>
                );
              })}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPageSafe + 1))}
              disabled={currentPageSafe === totalPages}
              style={{
                padding: '8px 12px',
                backgroundColor: currentPageSafe === totalPages ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: currentPageSafe === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              {t('common.next', { defaultValue: 'التالي' })}
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{
              backgroundColor: '#1b3350',
              border: '1px solid #333',
              borderRadius: '12px'
            }}>
              <div className="modal-header" style={{ border: 'none', borderBottom: '1px solid #333' }}>
                <h5 className="modal-title text-white" style={{ textAlign }}>
                  {t('permissions.delete.title', { defaultValue: 'تأكيد الحذف' })}
                </h5>
              </div>
              <div className="modal-body text-white" style={{ textAlign }}>
                <p>{t('permissions.delete.message', { defaultValue: 'هل أنت متأكد من حذف الصلاحية' })} "{permissionToDelete?.name}"؟</p>
              </div>
              <div className="modal-footer" style={{ border: 'none' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: '#6c757d',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px'
                  }}
                >
                  {t('btns.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: '#dc3545',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 20px'
                  }}
                >
                  {t('btns.delete', { defaultValue: 'حذف' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Permissions;

